import {Component, Input, OnInit, OnDestroy, ChangeDetectorRef} from '@angular/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { Subscription, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import {  CronJob, DEFAULT_LIMIT, DEFAULT_OFFSET, GetCronJobInstancesFn, CronJobInstanceWithCronJob } from '@portal-workspace/grow-shared-library';
import { FlexModule } from '@angular/flex-layout';
import { CustomContentLoaderComponent } from "../custom-content-loader-component/custom-content-loader.component";
import { CustomPaginatorComponent } from '../custom-paginator-component/custom-paginator/custom-paginator.component';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { loadingFor } from '@ngneat/loadoff';
import { DatePipe, AsyncPipe, } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { ApplicationTypeIconComponent } from '../application-type-icon/application-type-icon.component';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { animate, state, transition, style } from '@angular/animations';
import { trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';

 
@Component({
    selector: 'cron-job-instance',
    templateUrl: './cron-job-instance.component.html',
    styleUrls: ['./cron-job-instance.component.scss'],
    standalone: true,
    animations: [
      trigger('detailExpand', [
        state('collapsed', style({ height: '0px', minHeight: '0' })),
        state('expanded', style({ height: '*' })),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      ]),
    ],
    imports: [
      MatTableModule, 
      NgClass, 
      ExtendedModule, 
      FlexModule,
      CustomContentLoaderComponent,
      CustomPaginatorComponent,
      MatSortModule,
      DatePipe,
      AsyncPipe,
      MatButtonModule,
      MatTooltipModule,
      MatInputModule,
      MatFormFieldModule,
      ReactiveFormsModule,
      ApplicationTypeIconComponent,
      MatOptionModule,
      MatSelectModule,
      MatLabel,
      CommonModule // Add CommonModule here
    ]
})
export class CronJobInstanceComponent implements OnInit, OnDestroy {
   
  @Input({ required: true }) getCronJobInstancesFn!: GetCronJobInstancesFn;


  subscriptions: Subscription[] = [];
  moment = moment;
  formControlSearch!: FormControl<string | null>;

  total = 0;
  limit = DEFAULT_LIMIT;
  offset = DEFAULT_OFFSET;
  filter = '';
  loader = loadingFor('tableLoading');
  sorts: { prop: string, dir: 'asc' | 'desc' } | null = null;
  dataSource: CronJobInstanceWithCronJob[] = [];
  filteredDataSource: CronJobInstanceWithCronJob[] = [];
  displayedData: CronJobInstanceWithCronJob[] = [];
  columnsToDisplay = ['id','name','status','createdTime','completedTime'];
  cronJobs: CronJob[] = [];
  statuses: {type: string | null, name: string}[] = [
    {type: 'Success', name: 'success'},
    {type: 'Failed', name: 'failed'},
    {type: 'All', name: 'all'},
    {type: 'Started', name: 'started'}
  ];
  selectedCronJob: number | null = null;
  selectedStatus: string | null = null;
  expandedElementId: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
    private cdr: ChangeDetectorRef
  ) {
    this.formControlSearch = this.formBuilder.control(null);
     
  }
  private resetPagination() {
    this.offset = DEFAULT_OFFSET;
  }

  ngOnInit(): void {
    // Reset expanded state when component initializes
    this.expandedElementId = null;
    this.reload();

    this.subscriptions.push(
      this.formControlSearch.valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(r => {
          if (r) {
             this.filter = r;
             this.offset = DEFAULT_OFFSET;
             this.limit = DEFAULT_LIMIT;
             this.reload();
          } else {
            this.filter = '';
            this.offset = DEFAULT_OFFSET;
            this.limit = DEFAULT_LIMIT;
            this.reload();
          }
        })
      ).subscribe()
    )
    console.log('expandedElementId', this.expandedElementId);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions and reset state
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.expandedElementId = null;
  }

  reload() {
    // Explicitly reset expanded state
    this.expandedElementId = null;
    
    this.subscriptions.push(
      this.getCronJobInstancesFn(this.limit, this.offset, this.filter, this.selectedCronJob ?? null, this.selectedStatus ?? null).pipe(
        this.loader.tableLoading.track()
      ).subscribe(response => {
        if (!response) return;
        this.dataSource = response.payload;
        this.displayedData = this.dataSource;
        this.total = response.total;
        this.cronJobs = response.cronJobs;
        this.cronJobs.unshift({id: 0, name: 'All', description: '', route: '', category: '', createdTime: new Date()});
        this.limit = response.limit;
        this.offset = response.offset;
        
        // Ensure no rows are expanded after data load
        setTimeout(() => {
          this.expandedElementId = null;
          this.cdr.detectChanges();
        }, 0);
      })
    )
  }

  getColumnTitles(column: string): string {
    switch (column) {
      case 'name': return 'Name';
      case 'status': return 'Status';
      case 'createdTime': return 'Created Time';
      case 'completedTime': return 'Completed Time';
      case 'errorMessage': return 'Error Message';
      default: return column;
    }
  }

  initPagination() {
    this.total = this.total;
    this.limit = DEFAULT_LIMIT;
    this.offset = DEFAULT_OFFSET;
      
  }

  private reset() {
    this.limit = DEFAULT_LIMIT;
    this.offset = 0;
    this.expandedElementId = null;
  }
 
  onPagination($event: PageEvent) {
    this.limit = $event.pageSize;
    this.offset = $event.pageIndex;
    this.reload();
  }

  onCronJobSelected(cronJob: number | null) {
    console.log('onCronJobSelected', cronJob);
    this.selectedCronJob = cronJob;
    this.offset = DEFAULT_OFFSET;
    this.limit = DEFAULT_LIMIT;
    this.reload();
  }

  onStatusSelected(status: string) {
    this.selectedStatus = status;
    this.offset = DEFAULT_OFFSET;
    this.limit = DEFAULT_LIMIT;
     
    this.reload();
  }

  forceResetExpandedState() {
    this.expandedElementId = null;
    this.cdr.detectChanges();
  }

  trackByFn(index: number, item: CronJobInstanceWithCronJob): number {
    return item.id;
  }

  clickRow(element: CronJobInstanceWithCronJob) {
    console.log('clickRow', element);
    console.log('expandedElementId', this.expandedElementId);
    this.expandedElementId = this.expandedElementId === element.id ? null : element.id;
  }
}
